import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = '/api';

const ConflitosComerciais = () => {
  const [conflitos, setConflitos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);

  const carregar = async () => {
    try {
      const { data } = await axios.get(`${API}/commercial-conflicts`, {
        withCredentials: true,
      });
      setConflitos(data || []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao carregar conflitos comerciais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const resolver = async (id, action, item = null) => {
    const labels = {
      keep_current: 'manter com o corretor atual',
      transfer: 'transferir para o novo corretor',
      split_commission: 'dividir comissão',
    };

    let keepWith = null;

    if (action === 'split_commission') {
      const escolha = window.prompt(
        `Com quem o lead deve permanecer?\n\n1 - Corretor atual: ${item?.current_broker_name || 'Atual'}\n2 - Novo atendimento: ${item?.requested_broker_name || 'Novo'}\n\nDigite 1 ou 2:`
      );

      if (escolha === '1') keepWith = 'current';
      else if (escolha === '2') keepWith = 'requested';
      else return;
    } else {
      if (!window.confirm(`Confirmar decisão: ${labels[action]}?`)) return;
    }

    try {
      setResolving(true);
      const form = new FormData();
      form.append('action', action);
      if (keepWith) form.append('keep_with', keepWith);

      await axios.post(`${API}/commercial-conflicts/${id}/resolve`, form, {
        withCredentials: true,
      });

      toast.success('Conflito resolvido com sucesso');
      await carregar();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao resolver conflito');
    } finally {
      setResolving(false);
    }
  };

  const pendentes = conflitos.filter((c) => c.status === 'pending');
  const resolvidos = conflitos.filter((c) => c.status !== 'pending');

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Conflitos Comerciais</h1>
          <p className="text-slate-600 mt-1">
            Gestão de leads duplicados, propriedade comercial e atendimento presencial.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Pendentes</p>
            <p className="text-3xl font-bold text-orange-600 mt-1">{pendentes.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Resolvidos</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">{resolvidos.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{conflitos.length}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Conflitos pendentes</h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500">Carregando...</div>
          ) : pendentes.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Nenhum conflito pendente.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pendentes.map((item) => (
                <div key={item.id} className="p-5">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
                    <div className="space-y-2">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{item.lead_name}</h3>
                        <p className="text-sm text-slate-500">
                          Criado em: {item.created_at ? new Date(item.created_at).toLocaleString('pt-BR') : '-'}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                          <p className="text-xs text-slate-500">Corretor atual</p>
                          <p className="font-semibold text-slate-900">{item.current_broker_name || 'Não identificado'}</p>
                        </div>

                        <div className="bg-orange-50 rounded-lg border border-orange-200 p-3">
                          <p className="text-xs text-orange-700">Novo atendimento solicitado</p>
                          <p className="font-semibold text-slate-900">{item.requested_broker_name || 'Não identificado'}</p>
                        </div>
                      </div>

                      <p className="text-sm text-slate-600">
                        Origem: <strong>{item.source || '-'}</strong> • Empreendimento: <strong>{item.enterprise || '-'}</strong>
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row xl:flex-col gap-2 xl:w-64">
                      <button
                        disabled={resolving}
                        onClick={() => resolver(item.id, 'keep_current')}
                        className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Manter atual
                      </button>
                      <button
                        disabled={resolving}
                        onClick={() => resolver(item.id, 'transfer')}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        Transferir
                      </button>
                      <button
                        disabled={resolving}
                        onClick={() => resolver(item.id, 'split_commission', item)}
                        className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Dividir comissão
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ConflitosComerciais;
